import { getTokenInfo } from "../AuthManager/Auth";
import { getIsCypressRunning } from "constants/constants";
import LogRocket from "logrocket";
import {
  putErrorInfoInLocationState,
  QueryEndpointType,
  getInstantRetriesFromLocalStorage,
  incrementInstantRetriesLocalStorage,
} from "./errorManagerUtils";
import { ApolloError } from "@apollo/client";
import { ONE_SECOND_OF_MS } from "utils/timeUtils";
import { Auth0ContextInterface } from "@auth0/auth0-react";

/**
 * different behaviours based on the error code in attempt to resolve the error or display relevant info on the error page
 */
// tslint:disable-next-line: cognitive-complexity
export function handleErrorByCode({
  code,
  navigate,
  reload,
  auth0Loading,
  queryEndpoint,
  err,
  getAccessTokenSilently,
}: {
  code: number;
  navigate: any;
  reload: () => void;
  auth0Loading: boolean;
  queryEndpoint: QueryEndpointType;
  err: ApolloError;
  getAccessTokenSilently: Auth0ContextInterface["getAccessTokenSilently"];
}) {
  const { saveTokenInLocalStorage } = getTokenInfo();
  LogRocket.track(`ERROR_CODE_${code}`);

  // if error code is undefined, skip all of this and do nothing
  if (typeof code === "number") {
    switch (code) {
      case 404: {
        // 404 not found -> redirect to home
        // (this is also handled in App's <Switch> router, this is just a back-up!)
        navigate("/", { replace: true });
        break;
      }
      case 400: {
        break;
      }
      case 401: {
        // 400 bad request

        // --> if queryEndpoint is undefined (headers.queryName did not exist), reload page and skip incrementing retries
        if (!queryEndpoint) {
          reload();
          break;
        }

        // --> try to reauth silently
        // --> or show error page
        const retries = getInstantRetriesFromLocalStorage(queryEndpoint);
        const isCypressRunning = getIsCypressRunning();
        const { isTokenValid, clearTokenFromLocalStorage } = getTokenInfo();
        // if last login is still valid, try reloading (once)
        if (isTokenValid && retries < 1) {
          incrementInstantRetriesLocalStorage(queryEndpoint);
          reload();
        } else if (retries <= queryEndpoint.maxInstant401Retries) {
          // might be a bad token, so if we are now at the max number of retries,
          // then clear and invalidate the token before trying to silently authenticate again
          if (retries === queryEndpoint.maxInstant401Retries) {
            clearTokenFromLocalStorage();
          }

          // then try a silent reauth,
          const shouldTrySilentReauth = !auth0Loading || isCypressRunning;
          if (shouldTrySilentReauth) {
            console.log(
              `${
                retries === queryEndpoint.maxInstant401Retries
                  ? "🔑🚫: Invalid credentials,"
                  : "🔑: Token may still be valid, "
              } trying silent reauth,  🔄 retries: ${retries}`
            );
            incrementInstantRetriesLocalStorage(queryEndpoint);
            // get token silently, save to local storage and reload
            getAccessTokenSilently({ detailedResponse: true }).then(
              ({ access_token, expires_in, id_token, scope }) => {
                saveTokenInLocalStorage(
                  id_token,
                  `${Date.now() + expires_in * 1000}`
                );
                setTimeout(reload, ONE_SECOND_OF_MS);
              }
            );
          }
        } else {
          // then show the error page
          putErrorInfoInLocationState(navigate, code, err);
        }
        break;
      }
      case 408: {
        break;
      }
      // TODO: if 408, request timeout --> resend request?
      case 500: {
        break;
      }
      case 502: {
        break;
      }
      case 503: {
        break;
      }
      default: {
        console.log(
          `We do not have error handling setup for ${code}. Please notify the Flight team of this error code: ${code}`
        );
      }
    }
  }
}
